import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';

// Components
import NvFormDropdown from 'shared/components/inputs/nv-form-dropdown';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import { NvDropdownTextItem } from 'shared/components/inputs/nv-dropdown';

// Shared hook and styles
import {
  createLiveEventFilterStyles,
  useLiveEventFilter,
  getFormValueId
} from './use-live-event-filter';

const styles = createLiveEventFilterStyles('live-event-session-registered');

const FIELDS_TO_CLEAR = ['liveEventSessionRegisteredEvent', 'liveEventSessionRegisteredSession'];

const LiveEventSessionRegistered = () => {
  const { watch, setValue } = useFormContext();

  const [checkedValue, liveEvent, session, notRegisteredChecked, notRegisteredEvent] = watch([
    'liveEventSessionRegisteredChecked',
    'liveEventSessionRegisteredEvent',
    'liveEventSessionRegisteredSession',
    'liveEventNotRegisteredChecked',
    'liveEventNotRegisteredEvent',
  ]);

  const isEnabled = checkedValue;
  const isNotRegisteredFilterEnabled = notRegisteredChecked;

  const { liveEventsList, noLiveEvents, buildDropdownOptions, clearFields } = useLiveEventFilter(isEnabled, {
    fieldsToClear: FIELDS_TO_CLEAR,
  });

  // Build live event dropdown options, excluding events that are selected in the not-registered filter
  const liveEventOptions: NvDropdownTextItem[] = useMemo(() => {
    let filteredList = liveEventsList;

    // If a live event is selected in the not-registered filter, exclude it from the registered options
    if (isNotRegisteredFilterEnabled && notRegisteredEvent) {
      const notRegisteredEventId = getFormValueId(notRegisteredEvent);

      // Exclude the live event that is selected in the not-registered filter
      filteredList = liveEventsList.filter(
        (liveSession) => liveSession.id !== notRegisteredEventId
      );
    }

    return buildDropdownOptions(filteredList, () => {
      setValue('liveEventSessionRegisteredSession', null, { shouldValidate: true, shouldDirty: true });
    });
  }, [liveEventsList, isNotRegisteredFilterEnabled, notRegisteredEvent, buildDropdownOptions, setValue]);

  // Build session dropdown options based on selected live event
  const sessionOptions: NvDropdownTextItem[] = useMemo(() => {
    if (!liveEvent) return [];

    const liveEventId = getFormValueId(liveEvent);
    const selectedLiveEvent = liveEventsList.find((item) => item.id === liveEventId);
    const sessions = selectedLiveEvent?.sessions ?? [];

    return sessions.map((sessionItem) => ({
      id: sessionItem.id,
      type: 'text' as const,
      text: sessionItem.title,
      value: sessionItem.id,
    }));
  }, [liveEvent, liveEventsList]);

  // Auto-select parent event when session is set but event is not (e.g., when loading from saved data)
  useEffect(() => {
    if (!session || liveEvent || !isEnabled) {
      return;
    }

    const sessionId = getFormValueId(session);
    const parentEvent = liveEventsList.find((item) => item.sessions?.some((s) => s.id === sessionId));

    if (parentEvent) {
      setValue(
        'liveEventSessionRegisteredEvent',
        { id: parentEvent.id, value: parentEvent.id },
        { shouldValidate: true, shouldDirty: false }
      );
    }
  }, [session, liveEvent, liveEventsList, setValue, isEnabled]);

  // Clear live event and session if they become excluded due to a live event being selected in the not-registered filter
  useEffect(() => {
    if (isEnabled && liveEvent && isNotRegisteredFilterEnabled && notRegisteredEvent) {
      const notRegisteredEventId = getFormValueId(notRegisteredEvent);
      const currentEventId = getFormValueId(liveEvent);

      // Check if the currently selected live event is the one selected in the not-registered filter
      if (currentEventId === notRegisteredEventId) {
        clearFields(FIELDS_TO_CLEAR);
      }
    }
  }, [isEnabled, liveEvent, isNotRegisteredFilterEnabled, notRegisteredEvent, clearFields]);

  return (
    <div css={styles} className={`px-4 py-2 mb-4 live-event-session-registered ${isEnabled ? 'on' : ''}`}>
      <div className='inline-block mr-2 checkbox-wrapper'>
        <NvCheckbox
          withForm
          name='liveEventSessionRegisteredChecked'
          label={t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.HAS_REGISTERED()}
          labelClassName='text-large-regular pl-6 mr-2'
        />
      </div>
      {noLiveEvents && (
        <div className='info text-small text-gray-2 font-weight-bold'>
          {t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.NO_MULTI_SESSION()}
        </div>
      )}
      <div className='dropdowns ml-6'>
        <NvFormDropdown
          name='liveEventSessionRegisteredEvent'
          className='live-event'
          items={liveEventOptions}
          title={t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.SELECT_EVENT()}
        />
        <NvFormDropdown
          maxWidth100
          name='liveEventSessionRegisteredSession'
          className='session ml-2'
          items={sessionOptions}
          title={t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.SELECT_SESSION()}
          disabled={liveEvent == null}
        />
      </div>
    </div>
  );
};

export default LiveEventSessionRegistered;
